import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, XCircle, Building2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { LanguageToggle } from "../components/LanguageToggle";

export default function InvitePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { registerViaInvite } = useAuth();
  const { t } = useLang();
  const [info, setInfo] = useState({ state: "loading", orgName: "", message: "" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/invitations/${code}`)
      .then((res) => setInfo({ state: "valid", orgName: res.data.org_name, message: "" }))
      .catch((err) => setInfo({ state: "invalid", orgName: "", message: errorMessage(err, t("Invitasjonen er ugyldig")) }));
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t("Passordet må være minst 8 tegn"));
      return;
    }
    setSubmitting(true);
    try {
      const org = await registerViaInvite(code, name.trim(), email.trim(), password);
      toast.success(t("Velkommen! Du administrerer nå {name}", { name: org.org_name }));
      navigate("/dashboard");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background hero-decor flex flex-col">
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="invite-logo-link">
          <Logo showTagline />
        </Link>
        <LanguageToggle />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md border-border shadow-lg">
          {info.state === "loading" ? (
            <CardContent className="py-16 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
            </CardContent>
          ) : info.state === "invalid" ? (
            <CardContent className="py-12 text-center space-y-4" data-testid="invite-invalid-state">
              <XCircle className="w-10 h-10 mx-auto text-destructive" />
              <div>
                <h1 className="text-xl font-heading font-semibold">{t("Invitasjonen er ikke gyldig")}</h1>
                <p className="text-sm text-muted-foreground mt-1">{info.message}</p>
              </div>
              <Button asChild variant="secondary" data-testid="invite-home-button">
                <Link to="/"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Til forsiden")}</Link>
              </Button>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-2xl font-heading">{t("Bli med i teamet")}</CardTitle>
                <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="invite-org-name">
                  <Building2 className="w-4 h-4" />
                  {t("Du er invitert til å administrere")} <span className="font-medium text-foreground">{info.orgName}</span>
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="inv-name">{t("Navn")}</Label>
                    <Input id="inv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Fornavn og etternavn")} required data-testid="invite-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="inv-email">{t("E-post")}</Label>
                    <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="din@epost.no" required data-testid="invite-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="inv-password">{t("Velg passord")}</Label>
                    <Input id="inv-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Minst 8 tegn")} required minLength={8} data-testid="invite-password-input" />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={submitting} data-testid="invite-submit-button">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("Oppretter tilgang...")}</> : t("Få tilgang")}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
